/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import qs from 'qs';
import {
  fromBooleanString,
  isBooleanString,
  isFalse,
  isTrue
} from './BooleanUtils';

export const DEFAULT_QUERY_PARAMS = ['environment', 'oidcEnabled', 'features'];
/**
 * Provides a utility for safely retrieving the (raw) application query string.
 * When a URL is provided, extracts and returns the query string from that URL.
 *
 * ```typescript
 *   import {getQueryString} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?debug=true&userId=4321&accountName=Some%20Account
 *   const search = getQueryString(); // ?debug=true&userId=4321&accountName=Some%20Account
 *
 *   // extract from specific URL ...
 *   const urlSearch = getQueryString('https://example.com?param=value'); // ?param=value
 * ```
 *
 * @param url Optional URL to extract query string from
 * @returns The query string from the URL or current location
 */
export const getQueryString = (url?: string): string => {
  if (url) {
    const queryIndex = url.indexOf('?');
    return queryIndex !== -1 ? url.substring(queryIndex) : '';
  }
  return globalThis?.location?.search || '';
};

/**
 * Provides a utility which allows for retrieving a specific parameter from the
 * application query string. By default, this function will return the value of
 * the specified parameter. If the `pair` argument is set to true, both the key
 * and value pair for the query parameter will be returned as an object.
 *
 * ```typescript
 *   import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?debug=true&userId=4321&accountName=Some%20Account
 *   const search = getParameter(debug); // true
 *   const search = getParameter(debug, true); // {debug: true}
 * ```
 *
 * @returns
 */
export const getParameter = (param: string, pair = false) => {
  const parsed = (qs.parse(getQueryString(), {ignoreQueryPrefix: true}) || {});
  if (param) {
    let value: any = parsed[param];
    if (!isBooleanString(value)) {
      if (!isNaN(value)) {
        value = Number(value as string);
      }
    } else {
      value = fromBooleanString(value as string);
    }
    return pair ? {[param]: value} : value;
  }
};

/**
 * Provides a utility which allows for retrieving a default parameters from the
 * application query string.
 *
 * ```typescript
 *   import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?environment=us&debug=true&userId=4321&accountName=Some%20Account
 *   const search = getDefaultParameters(); // {environment: 'us'}
 * ```
 *
 * @returns
 */
export const getDefaultParameters = () => {
  const results = {};
  for (const param of DEFAULT_QUERY_PARAMS) {
    const result = getParameter(param, true);
    const value = result[param];
    if (value) {
      Object.assign(results, result);
    }
  }
  return results;
};

/**
 * Provides a utility which removes the query string from the given url (if
 * present), and returns the resulting url with the query string removed.
 *
 * ```typescript
 *   import {removeQueryString} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   const uri = 'https://admin.calamp.com?env=us&debug=true&features=apm,autojobs';
 *   const url = removeQueryString(uri); // https://admin.calamp.com
 * ```
 *
 * @param   url The url for which the query string is to be removed (if included).
 * @returns The resulting url with the query string removed
 */
export const removeQueryString = (url: string): string => {
  const qs = url.indexOf('?');
  if (qs > -1) {
    return url.substring(0, qs);
  }
  return url;
};

/**
 * Extracts and returns query parameters from a given URL `string`.
 *
 * This utility function takes a URL string, parses its query parameters, and
 * returns an object containing key-value pairs. It attempts to convert numeric
 * values to numbers; otherwise, it stores the values as strings.
 *
 * ```typescript
 *  import {extractQueryParams} from '@ion/core/src/utils/QueryStringUtils';
 *  ...
 *
 *  const root   = 'https://app.calamp.com';
 *  const url    = '${root}?env=us&debug=true&userId=4321&accountName=Some%20Account';
 *  const params = extractQueryParams(url);
 *  console.log(params);
 *  // Output:
 *  // {
 *  //   env         : 'us',
 *  //   debug       : 'true',
 *  //   userId      : 4321,
 *  //   accountName : 'Some Account'
 *  // }
 * ```
 *
 * @param   url The URL string from which query parameters are to be extracted.
 * @returns An object with key-value pairs of query parameters, where numbers are
 *          parsed as `number` and other values are stored as `string`.
 */
export const extractQueryParams = (url: string): Record<string, any> => (
  qs.parse(new URL(url).search, {
    ignoreQueryPrefix : true,
    decoder           : (value) => {
      const decodedValue = decodeURIComponent(value);
      if (/^-?\d+(\.\d+)?$/.test(decodedValue)) {
        return parseFloat(decodedValue);
      } else if (isTrue(decodedValue)) {
        return true;
      } else if (isFalse(decodedValue)) {
        return false;
      }
      return decodedValue;
    }
  })
);

/**
 * Preserves the current query string parameters and appends them to the provided
 * URL.
 *
 * This utility accepts a URL and appends the application's current query string
 * parameters to it. If no query parameters exist in the current application URL,
 * the original URL is returned unchanged.
 *
 * When the `honorURL` argument is set to `true`, any query parameters that exist
 * in the provided URL will be preserved, followed by the application's current
 * query parameters.
 *
 * ```typescript
 *  import {preserveParams} from '@ion/core/src/utils/QueryStringUtils';
 *
 *  // current url:
 *  https://ion.calamp.com/admin/assets?env=us&debug=true&features=apmdebug
 *
 *  preserveParams('https://ion.calamp.com');
 *  // https://ion.calamp.com?env=us&debug=true&features=apmdebug
 *
 *  preserveParams('https://ion.calamp.com/reports?reportId=123', true);
 *  // https://ion.calamp.com/reports?reportId=123&env=us&debug=true&features=apmdebug
 * ```
 *
 * @param   url The URL string to which the current query parameters will be appended.
 * @param   honorURL When true, preserves any query parameters in the provided URL.
 *          Defaults to `false`.
 * @returns The URL with the current query parameters appended.
 */
export const preserveParams = (url: string, honorURL = false): string => {
  const params = getQueryString();
  if (params) {
    if (honorURL && url.includes('?')) {
      return `${url}${params.replace('?', '&')}`;
    }
    return `${url}${params}`;
  }
  return url;
};
